import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'i18n'
import LegacyRetinaImage from 'components/LegacyRetinaImage'
import PlaceHolderImage from 'components/PlaceholderImage'
import { isOfTypeImage } from 'util/quiz-trivia'
import ProgressBar from 'steps/shared/ProgressBar'
import {
  getQuestionBackgroundImageId,
  getMobileQuestionBackgroundImageId,
  getQuestionBackgroundImageAlt,
  getMobileQuestionBackgroundImageAlt,
  getQuestionBackgroundColor,
  debugLogQuestionBackground,
} from './question-background-utils'

const QuestionSlide = ({
  question,
  currentPosition,
  totalQuestions,
  onAnswerClick,
  selectedAnswerId,
  backgroundColor,
  backgroundImage,
  skipButton,
  nextStepButton,
  // puck (BR-5451): container-level presentation options
  hideProgressBar = false,
  answerHoverColor = undefined,
}) => {
  const { t } = useTranslation('personalityQuiz')
  const [isDisabled, setIsDisabled] = useState(false)
  const progressBarRef = useRef(null)

  // Focus progress bar after answer is selected to guide screen reader users
  useEffect(() => {
    if (selectedAnswerId && progressBarRef.current) {
      progressBarRef.current.focus()
    }
  }, [selectedAnswerId])

  const quizHeaderImageURL =
    question.image &&
    question.image.id &&
    `${process.env.SITE_ASSET_URL}/${question.image.id}`

  // puck (BR-5451): header media arrives as a rendered node, backgrounds as
  // resolved urls/colors (no beaker image entities behind a puck step)
  const hasHeader = !!(question.puckHeader || quizHeaderImageURL)

  // Get question-level background image - no fallback to step background
  const questionBackgroundImageId = getQuestionBackgroundImageId(question, null)
  const mobileQuestionBackgroundImageId = getMobileQuestionBackgroundImageId(question, null)
  
  // Use only question-level backgrounds - step background is decorative only
  const effectiveBackgroundImage = questionBackgroundImageId
    ? { id: questionBackgroundImageId }
    : null
  
  const effectiveMobileBackgroundImage = mobileQuestionBackgroundImageId
    ? { id: mobileQuestionBackgroundImageId }
    : null

  const questionBackgroundColor = getQuestionBackgroundColor(question, backgroundColor)
  const questionBackgroundImageAlt = getQuestionBackgroundImageAlt(question, backgroundImage)
  const mobileQuestionBackgroundImageAlt = getMobileQuestionBackgroundImageAlt(question, null)

  // Debug logging for troubleshooting background image issues
  debugLogQuestionBackground(question, currentPosition, backgroundImage)

  const desktopBgUrl =
    question.puckBackgroundImageUrl ??
    (effectiveBackgroundImage
      ? `${process.env.SITE_ASSET_URL}/${effectiveBackgroundImage.id}`
      : null)
  const mobileBgUrl =
    question.puckMobileBackgroundImageUrl ??
    (effectiveMobileBackgroundImage
      ? `${process.env.SITE_ASSET_URL}/${effectiveMobileBackgroundImage.id}`
      : null) ??
    desktopBgUrl
  // puck questions: bg only when explicitly set (no #1a1a1a fallback)
  const bgColor = question.puckMode
    ? question.puckBackgroundColor
    : question.puckBackgroundColor ?? questionBackgroundColor

  // we all hate this, but it's the only way to get style data from beaker to be applied to desktop-only
  const inlineStyle = `
         @media (min-width: 900px) {
            .quiz__item-${currentPosition} {
            ${bgColor ? `background-color: ${bgColor};` : ``}
            ${desktopBgUrl ? `background: url(${desktopBgUrl});` : ``}
            }
        }

        @media (max-width: 900px) {
            .quiz__item-${currentPosition} {
            ${mobileBgUrl ? `background: url(${mobileBgUrl});` : ``}
            }
        }
      `

  return (
    <div
      className={`step-3-quiz-item quiz__item bg-img quiz__item-${currentPosition} with-shadow`}
      key={question.id}
      // puck: the outer slide carries --step-container-background-color; make
      // it transparent (or the slide bg color) so only the inner card paints
      style={
        question.puckMode
          ? { backgroundColor: bgColor || 'transparent' }
          : undefined
      }
    >
      <style
        dangerouslySetInnerHTML={{
          __html: inlineStyle,
        }}
      />

      <div
        className={`quiz__question ${
          hasHeader ? ' question-image' : ' no-question-img'
        }`}
        // puck (BR-5451): question-card background/border/shadow
        style={question.puckCardStyle}
      >
        {question.puckCardOverlayStyle && (
          <div aria-hidden style={question.puckCardOverlayStyle} />
        )}
        <div
          className={`quiz__question-header`}
          // puck (BR-5451): fixed header height (image crops)
          style={
            question.puckHeaderHeight
              ? { height: question.puckHeaderHeight, overflow: 'hidden' }
              : undefined
          }
        >
          {!hideProgressBar && (
            <ProgressBar
              ref={progressBarRef}
              label={t('label')}
              position={currentPosition}
              total={totalQuestions}
              isEditorialWithoutImage={hasHeader}
            />
          )}
          {question.puckHeader ? (
            <div className="quiz__question-header-image ">
              {question.puckHeader}
            </div>
          ) : (
            quizHeaderImageURL && (
              <div className="quiz__question-header-image ">
                <PlaceHolderImage
                  src={quizHeaderImageURL}
                  height={227}
                  width={745}
                  alt={question.image.altAttribute || ''}
                />
              </div>
            )
          )}
        </div>
        <div className={`quiz__question-content`}>
          {/* Hidden img tag for background image accessibility - only rendered if NO question image exists */}
          {effectiveBackgroundImage && (
            <img
              style={{ display: 'none' }}
              src={`${process.env.SITE_ASSET_URL}/${effectiveBackgroundImage.id}`}
              alt={quizHeaderImageURL ? '' : (questionBackgroundImageAlt || mobileQuestionBackgroundImageAlt || '')}
              aria-hidden="true"
              role="presentation"
            />
          )}
          <h2
            className={`quiz-question-headline`}
            tabIndex={0}
            aria-label={question.text}
            style={question.puckHeadlineStyle}
            dangerouslySetInnerHTML={{ __html: question.text }}
          ></h2>
          <ul
            className={`quiz__question-list${
              isOfTypeImage(question) ? ' image-choices' : ''
            }${answerHoverColor || question.puckSelectedAnswerColor ? ' puck-pq-answers' : ''}`}
            style={{
              ...(answerHoverColor
                ? { '--pq-answer-hover-bg': answerHoverColor }
                : {}),
              ...(question.puckSelectedAnswerColor
                ? { '--pq-answer-selected': question.puckSelectedAnswerColor }
                : {}),
              // puck (BR-5451): answers list flex controls
              ...(question.puckAnswersStyle ?? {}),
            }}
          >
            {question.answers.map((answer, i) => {
              const inputId = `input-${question.id}-${answer.id}`
              if (answer.type === 'TEXT') {
                return (
                  <li
                    tabIndex={0}
                    key={i}
                    className={`${
                      selectedAnswerId === answer.id ? 'selected' : ''
                    } pointer`}
                    style={
                      question.puckAnswerWidth
                        ? { width: question.puckAnswerWidth }
                        : undefined
                    }
                    onKeyUp={e => {
                      if (e.key === 'Enter') {
                        setIsDisabled(true)
                        setTimeout(() => setIsDisabled(false), 1200)
                        onAnswerClick({
                          questionId: question.id,
                          answerId: answer.id,
                          answers: question.answers,
                          totalQuestions: question.answers.length,
                          currentPosition: currentPosition,
                          progressBarRef: progressBarRef,
                        })
                      }
                    }}
                  >
                    <label
                      className="custom-btn-label"
                      htmlFor={inputId}
                      // puck answers carry explicit colors; legacy answers
                      // keep their scss-var styling
                      style={answer.puckStyle}
                    >
                      <input
                        id={inputId}
                        type="radio"
                        name={question.id}
                        disabled={isDisabled}
                        onClick={() => {
                          setIsDisabled(true)
                          setTimeout(() => setIsDisabled(false), 1200)
                          onAnswerClick({
                            questionId: question.id,
                            answerId: answer.id,
                            answers: question.answers,
                            totalQuestions: question.answers.length,
                            currentPosition: currentPosition,
                            progressBarRef: progressBarRef,
                          })
                        }}
                      />
                      <span
                        dangerouslySetInnerHTML={{ __html: answer.text }}
                      ></span>
                    </label>
                  </li>
                )
              }
              if (answer.type === 'IMAGE') {
                return (
                  <li
                    tabIndex={0}
                    key={i}
                    className={`${
                      selectedAnswerId === answer.id ? 'selected' : ''
                    } pointer`}
                    style={
                      question.puckAnswerWidth
                        ? { width: question.puckAnswerWidth }
                        : undefined
                    }
                    role="button"
                    onKeyUp={e => {
                      if (e.key === 'Enter') {
                        onAnswerClick({
                          questionId: question.id,
                          answerId: answer.id,
                          answers: question.answers,
                          totalQuestions: question.answers.length,
                          currentPosition: currentPosition,
                          progressBarRef: progressBarRef,
                        })
                      }
                    }}
                    onClick={() => {
                      onAnswerClick({
                        questionId: question.id,
                        answerId: answer.id,
                        answers: question.answers,
                        totalQuestions: question.answers.length,
                        currentPosition: currentPosition,
                        progressBarRef: progressBarRef,
                      })
                    }}
                  >
                    <div className="image-choice">
                      {answer.puckMedia ?? (
                        <LegacyRetinaImage image={answer.image} />
                      )}
                    </div>
                  </li>
                )
              }

              return <></>
            })}
          </ul>

          {skipButton}
        </div>
      </div>
      {nextStepButton}
    </div>
  )
}

export default QuestionSlide
